import type {IEnvironment} from 'relay-runtime';
import {readProjectStages, stageWithProfile} from '../../forecast-app/shared/util/ProjectStages';
import type {ProjectStage} from '../../forecast-app/shared/util/ProjectStages';
import type {StageProfile} from '../../forecast-app/shared/util/projectStageUtil';
import {trackEvent} from './TrackingV2';

type StageChangeInput = {
	id?: string | null;
	project?: {id: string} | null;
	stageId?: string | null;
	status?: string | null;
};

export type ProjectStageChangedProperties = {
	stageProfile: StageProfile | null;
	previousStageProfile: StageProfile | null;
	profileChanged: boolean | null;
	stagePosition: number | null;
	stageCount: number | null;
	changedBy: 'Stage' | 'Status';
};

type StoredStage = {id: string; profile: StageProfile | null};

/** The project's stage as the Relay store holds it before the update, or null when the project isn't loaded. */
const storedStageOf = (projectId: string | undefined, environment: IEnvironment): StoredStage | null => {
	if (!projectId) {
		return null;
	}
	const source = environment.getStore().getSource();
	const stageRef = (source.get(projectId)?.stage as {__ref?: string} | null | undefined)?.__ref;
	const stage = stageRef ? source.get(stageRef) : null;
	if (!stageRef || !stage) {
		return null;
	}
	return {id: stageRef, profile: (stage.profile as StageProfile | null | undefined) ?? null};
};

/**
 * The properties of `Project Stage Changed` for an UpdateProjectMutation input, or null when the input doesn't change
 * the project's stage. No stage names or ids: names are admin-entered text (spec 010).
 */
export const projectStageChangedProperties = (
	input: StageChangeInput,
	environment: IEnvironment
): ProjectStageChangedProperties | null => {
	if (!input.stageId && !input.status) {
		return null;
	}
	const changedBy = input.stageId ? 'Stage' : 'Status';
	const stages = readProjectStages(environment);
	const newStage: ProjectStage | null = input.stageId
		? stages.find(stage => stage.id === input.stageId) ?? null
		: stageWithProfile(input.status as StageProfile, environment);
	const stageProfile = newStage?.profile ?? (input.status as StageProfile | null | undefined) ?? null;

	const previous = storedStageOf(input.project?.id ?? input.id ?? undefined, environment);
	if (previous && changedBy === 'Stage' && previous.id === input.stageId) {
		return null;
	}
	// The server keeps the project's stage when a status-only write matches its current profile, so it isn't a change.
	if (previous && changedBy === 'Status' && previous.profile === stageProfile) {
		return null;
	}

	const position = newStage ? stages.findIndex(stage => stage.id === newStage.id) : -1;
	return {
		stageProfile,
		previousStageProfile: previous?.profile ?? null,
		profileChanged: previous?.profile && stageProfile ? previous.profile !== stageProfile : null,
		stagePosition: position >= 0 ? position + 1 : null,
		stageCount: stages.length > 0 ? stages.length : null,
		changedBy,
	};
};

export const trackProjectStageChanged = (input: StageChangeInput, environment: IEnvironment): void => {
	const properties = projectStageChangedProperties(input, environment);
	if (properties) {
		trackEvent('Project Stage', 'Changed', properties);
	}
};
